import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelfieImage } from "@/components/SelfieImage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MissingChannelsAlert } from "@/components/MissingChannelsAlert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { formatDuration, formatDateTime, formatTime, STATUS_META } from "@/lib/format";
import { useRealtime } from "@/lib/realtime";
import { AppNav } from "@/components/AppNav";
import {
  Fingerprint, LogOut, Users, Activity, Coffee, AlertTriangle, Download,
  UserPlus, Trash2, Clock, MapPin, Loader2, Camera, ShieldCheck,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, tone, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid={testid}>
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
          <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${tone}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <div className="mt-3 font-display text-3xl font-bold text-white">{value}</div>
      </div>
    </motion.div>
  );
}

function SelfieModal({ record }) {
  if (!record?.selfie_path) return <span className="text-xs text-slate-500">No photo</span>;
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="group relative" data-testid="view-selfie-btn">
          <SelfieImage path={record.selfie_path} className="h-10 w-10 rounded-lg object-cover ring-1 ring-slate-700 group-hover:ring-indigo-400 transition" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-4 w-4 text-indigo-600" /> Selfie proof — {record.user_name}
          </DialogTitle>
          <DialogDescription>Verification photo and metadata for this attendance event.</DialogDescription>
        </DialogHeader>
        <SelfieImage path={record.selfie_path} className="w-full rounded-xl object-cover aspect-video" />
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Type</span><span className="font-medium capitalize">{record.type.replace("_", " ")}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Time</span><span className="font-mono">{formatDateTime(record.ts)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Zone</span><span>{record.work_zone}</span></div>
          {record.note && <div className="pt-2 text-slate-600 border-t"><span className="text-slate-400 text-xs">Note: </span>{record.note}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ZoneDialog({ s, onSaved }) {
  const [open, setOpen] = useState(false);
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState(500);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLat(s.home_zone?.lat ?? "");
      setLng(s.home_zone?.lng ?? "");
      setRadius(s.home_zone?.radius_m ?? 500);
    }
  }, [open, s]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (p) => { setLat(p.coords.latitude.toFixed(6)); setLng(p.coords.longitude.toFixed(6)); },
      () => toast.error("Could not get your location")
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/staff/${s.id}/zone`, { lat: lat === "" ? null : Number(lat), lng: lng === "" ? null : Number(lng), radius_m: Number(radius) || 500 });
      toast.success("Home zone saved");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/staff/${s.id}/zone`, { lat: null, lng: null });
      toast.success("Home zone cleared");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-slate-500 hover:text-indigo-400" title="Set home zone" data-testid={`zone-staff-${s.id}`}>
          <MapPin className="h-3.5 w-3.5" />
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Home zone — {s.name}</DialogTitle>
          <DialogDescription>Check-ins outside this radius are automatically flagged as out of zone.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {s.home_zone && (
            <p className="text-xs text-emerald-600" data-testid="zone-active-info">
              Active: {Number(s.home_zone.lat).toFixed(5)}, {Number(s.home_zone.lng).toFixed(5)} · radius {s.home_zone.radius_m} m
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label>Latitude</Label>
              <Input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="-6.200000" data-testid="zone-lat-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Longitude</Label>
              <Input value={lng} onChange={(e) => setLng(e.target.value)} placeholder="106.816666" data-testid="zone-lng-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Radius (meters)</Label>
            <Input type="number" min="50" value={radius} onChange={(e) => setRadius(e.target.value)} data-testid="zone-radius-input" />
          </div>
          <Button variant="outline" onClick={useMyLocation} className="w-full" data-testid="zone-use-location-btn">
            <MapPin className="h-4 w-4" /> Use my current location
          </Button>
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving || lat === "" || lng === ""} className="flex-1" data-testid="zone-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save zone"}
            </Button>
            {s.home_zone && <Button variant="destructive" onClick={clear} disabled={saving} data-testid="zone-clear-btn">Clear</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [staff, setStaff] = useState([]);
  const [records, setRecords] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", property_ids: [] });
  const [saving, setSaving] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    try {
      const recParams = {};
      if (dateFrom) recParams.date_from = dateFrom;
      if (dateTo) recParams.date_to = dateTo;
      const [s, st, rec, pr] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/staff"),
        api.get("/admin/attendance", { params: recParams }),
        api.get("/properties"),
      ]);
      setStats(s.data);
      setStaff(st.data.staff);
      setRecords(rec.data.records);
      setProperties(pr.data.properties);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useRealtime(useCallback((msg) => {
    if (!msg?.event) return;
    if (msg.event === "attendance.check") toast(`${msg.user_name} — ${String(msg.type || "").replace("_", " ")}`);
    if (msg.event === "leave.created") toast(`New leave request from ${msg.user_name}`);
    if (msg.event === "leave.reviewed") toast(`Leave request ${msg.status}`);
    load();
  }, [load]));

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // slow fallback; realtime pushes via WebSocket
    return () => clearInterval(t);
  }, [load]);

  const addStaff = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/staff", form);
      toast.success("Staff added");
      setForm({ name: "", email: "", password: "", property_ids: [] });
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeStaff = async (id) => {
    try {
      await api.delete(`/admin/staff/${id}`);
      toast.success("Staff removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get("/admin/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "attendance_report.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1F1B16]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1F1B16] dark-scroll text-slate-100">
      <AppNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 flex justify-end">
        <Button size="sm" onClick={exportCsv} className="bg-indigo-600 hover:bg-indigo-700" data-testid="admin-export-csv-btn">
          <Download className="h-4 w-4" /> Export Attendance CSV
        </Button>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* stats bento */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard icon={Users} label="Total staff" value={stats?.total_staff ?? 0} tone="bg-indigo-500/20 text-indigo-400" testid="stat-total" />
          <StatCard icon={Activity} label="Working now" value={stats?.working ?? 0} tone="bg-emerald-500/20 text-emerald-400" testid="stat-working" />
          <StatCard icon={Coffee} label="On break" value={stats?.on_break ?? 0} tone="bg-amber-500/20 text-amber-400" testid="stat-break" />
          <StatCard icon={Clock} label="Checked in today" value={stats?.checked_in_today ?? 0} tone="bg-sky-500/20 text-sky-400" testid="stat-checkedin" />
          <StatCard icon={AlertTriangle} label="Late today" value={stats?.late_today ?? 0} tone="bg-rose-500/20 text-rose-400" testid="stat-late" />
        </div>

        <MissingChannelsAlert dark />

        <div className="grid lg:grid-cols-3 gap-6">
          {/* chart */}
          <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h3 className="font-display font-semibold text-lg mb-1">Weekly work hours</h3>
            <p className="text-xs text-slate-400 mb-4">Total hours logged across the team, last 7 days</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats?.weekly_hours || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
                <XAxis dataKey="day" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "#1F1B16", border: "1px solid #1F2937", borderRadius: 12, color: "#fff" }}
                  cursor={{ fill: "#6366F110" }}
                />
                <Bar dataKey="hours" fill="#6366F1" radius={[6, 6, 0, 0]} maxBarSize={44} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* add staff */}
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 flex flex-col">
            <h3 className="font-display font-semibold text-lg mb-1">Team roster</h3>
            <p className="text-xs text-slate-400 mb-4">{staff.length} staff registered</p>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700 mb-4" data-testid="add-staff-btn">
                  <UserPlus className="h-4 w-4" /> Add staff member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add new staff</DialogTitle>
                  <DialogDescription>Create a staff account with a temporary password they can use to sign in.</DialogDescription>
                </DialogHeader>
                <form onSubmit={addStaff} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Full name</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="new-staff-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="new-staff-email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Temporary password</Label>
                    <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="new-staff-password" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Assigned properties</Label>
                    <div className="max-h-32 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1">
                      {properties.length === 0 && <p className="text-xs text-slate-400">No properties yet — add them on the Performance page.</p>}
                      {properties.map((p) => {
                        const checked = form.property_ids.includes(p.id);
                        return (
                          <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer py-0.5" data-testid={`assign-prop-${p.id}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => setForm({ ...form, property_ids: e.target.checked ? [...form.property_ids, p.id] : form.property_ids.filter((x) => x !== p.id) })}
                            />
                            {p.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={saving} data-testid="save-staff-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            <div className="space-y-2 overflow-y-auto max-h-[180px] dark-scroll">
              {staff.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm py-1.5 px-2 rounded-lg hover:bg-slate-800/50">
                  <span className="truncate">{s.name}{s.home_zone && <MapPin className="inline h-3 w-3 ml-1 text-emerald-400" />}</span>
                  <span className="flex items-center gap-2 flex-shrink-0">
                    <ZoneDialog s={s} onSaved={load} />
                    <button onClick={() => removeStaff(s.id)} className="text-slate-500 hover:text-rose-400" data-testid={`delete-staff-${s.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Live monitoring + records */}
        <Tabs defaultValue="live">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="live" data-testid="tab-live">Live monitoring</TabsTrigger>
            <TabsTrigger value="records" data-testid="tab-records">Attendance records</TabsTrigger>
          </TabsList>

          <TabsContent value="live" className="mt-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="live-grid">
              {staff.length === 0 && <p className="text-slate-400 text-sm">No staff yet. Add your first team member above.</p>}
              {staff.map((s) => {
                const meta = STATUS_META[s.status] || STATUS_META.checked_out;
                return (
                  <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl border border-slate-800 bg-slate-900 p-4" data-testid={`staff-tile-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        {s.last_event?.selfie_path ? (
                          <SelfieImage path={s.last_event.selfie_path} className="h-11 w-11 rounded-full object-cover ring-2 ring-slate-700" />
                        ) : (
                          <div className="h-11 w-11 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 font-semibold">
                            {s.name?.[0]?.toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-medium truncate">{s.name}</div>
                          <div className="text-xs text-slate-400 truncate">{s.email}</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.bg} ${meta.text}`} data-testid="staff-status-badge">
                        <span className="relative flex h-2 w-2">
                          {(s.status === "working" || s.status === "break") && <span className={`absolute inline-flex h-full w-full rounded-full ${meta.dot} opacity-75 animate-ping-slow`} />}
                          <span className={`relative inline-flex rounded-full h-2 w-2 ${meta.dot}`} />
                        </span>
                        {meta.label}
                      </span>
                      <span className="font-mono text-sm text-slate-300">{formatDuration(s.worked_seconds)}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {formatTime(s.check_in_time)}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Zone 4</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="records" className="mt-4">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 bg-slate-900 border-slate-700 text-slate-200" data-testid="records-date-from" />
              <span className="text-slate-500 text-xs">to</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 bg-slate-900 border-slate-700 text-slate-200" data-testid="records-date-to" />
              {(dateFrom || dateTo) && (
                <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="records-date-clear">Clear</Button>
              )}
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="records-table">
                  <thead>
                    <tr className="border-b border-slate-800 text-left text-slate-400">
                      <th className="p-3 font-medium">Photo</th>
                      <th className="p-3 font-medium">Staff</th>
                      <th className="p-3 font-medium">Type</th>
                      <th className="p-3 font-medium">Time</th>
                      <th className="p-3 font-medium">Zone</th>
                      <th className="p-3 font-medium">Location</th>
                      <th className="p-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.length === 0 && (
                      <tr><td colSpan={7} className="p-8 text-center text-slate-500">No attendance records yet.</td></tr>
                    )}
                    {records.map((r) => (
                      <tr key={r.id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                        <td className="p-3"><SelfieModal record={r} /></td>
                        <td className="p-3">
                          <div className="font-medium">{r.user_name}</div>
                          <div className="text-xs text-slate-400">{r.user_email}</div>
                        </td>
                        <td className="p-3 capitalize">{r.type.replace("_", " ")}</td>
                        <td className="p-3 font-mono text-slate-300">{formatDateTime(r.ts)}</td>
                        <td className="p-3 text-slate-400">{r.work_zone}</td>
                        <td className="p-3">
                          {r.lat != null && r.lng != null ? (
                            <a href={`https://www.google.com/maps?q=${r.lat},${r.lng}`} target="_blank" rel="noreferrer"
                              className="text-indigo-400 hover:underline inline-flex items-center gap-1 text-xs" data-testid={`map-link-${r.id}`}>
                              <MapPin className="h-3 w-3" /> Map
                            </a>
                          ) : (
                            <span className="text-xs text-slate-500">—</span>
                          )}
                          {r.out_of_zone && (
                            <span className="block text-rose-400 text-[11px] font-semibold mt-0.5" data-testid={`outzone-${r.id}`}>
                              Out of zone{r.zone_distance_m != null ? ` (${r.zone_distance_m} m)` : ""}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          {r.late && r.type === "check_in"
                            ? <span className="text-rose-400 text-xs font-medium">Late</span>
                            : <span className="text-emerald-400 text-xs font-medium">On time</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
